import { FALLBACK_CONFIG } from './fallback.js';
function hasDestination(app) {
    return app.href.trim().length > 0;
}
function entry(app, activeAppId) {
    return { app, active: app.id === activeAppId, navigable: hasDestination(app) };
}
function findApp(apps, id) {
    return apps.find((app) => app.id === id);
}
/**
 * The one filter, applied identically to the embedded fallback and to the fetched
 * payload: an entry renders only when it is enabled AND has a destination, so an
 * endpoint outage can never paint a dead tile.
 *
 * The host's own app is the single exemption from `enabled` — it is always
 * rendered and indicated, resolved from the fallback when the payload omits it,
 * because an app that hid itself from its own bar would leave the artist with no
 * indication of where they are and NavTrigger with no chip to dock. It is never
 * exempt from the destination requirement: without one it becomes an inert
 * indicator chip rather than a broken link.
 *
 * A LEGITIMATELY EMPTY payload (`apps: []`, which `normalize` now honours instead of
 * discarding) changes nothing here, deliberately. The empty array is a statement about
 * the CATALOG — the set of other apps a viewer may switch to — and the host's own entry
 * has never been part of that set; it is the one exemption from it. Both invariants
 * therefore hold at once: the catalog is empty, and the viewer still sees where they
 * are and NavTrigger still has a chip to dock. Dropping the active entry too would
 * trade a real regression (no orientation, a blank docked chip) for no gain, and the
 * resolver cannot even see the distinction — an empty fetched payload and an empty
 * `fallbackConfig` arrive here identically.
 *
 * Note this is not a licence to invent destinations: only the ids the compiled-in
 * FALLBACK_CONFIG actually carries can be resolved this way, which is why an empty
 * payload leaves a satellite host whose id is absent there with a genuinely empty bar
 * rather than guessing at a domain that may not exist on the stage yet.
 */
function resolveApps(config, activeAppId, fallback) {
    const visible = config.apps.filter((app) => app.id === activeAppId || (app.enabled && hasDestination(app)));
    const resolved = visible.map((app) => entry(app, activeAppId));
    if (resolved.some((item) => item.active)) {
        return resolved;
    }
    const embedded = findApp(fallback.apps, activeAppId) ?? findApp(FALLBACK_CONFIG.apps, activeAppId);
    if (!embedded) {
        return resolved;
    }
    return [entry(embedded, activeAppId), ...resolved];
}
export { hasDestination, resolveApps };
//# sourceMappingURL=resolve.js.map